import React from 'react';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { getNormalizedActiveLanguage, getPickersLocale } from 'helpers/locale';
import { GlobalStyles } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import Moment from 'moment';
import { getActiveLanguage } from '@haldor/ui';

/**
 * StartEndInputs
 * Props:
 * - input: { name, value, onChange } (react-final-form) OR you can pass value/onChange directly
 * - name
 * - value: { start: string, end: string }
 * - onChange: function(updatedValue)
 * - disabled: boolean
 * - labels: { startLabel, endLabel } optional
 */
const StartEndInputs = (props) => {
	const { input, name, value, onChange, disabled, labels } = props;

	const current = (input && input.value) || value || { start: '', end: '' };

	const emitChange = (next) => {
		if (input && input.onChange) {
			input.onChange(next);
		}
		if (onChange) onChange(next);
	};

	const setField = (field, v) => {
		const next = { ...(current || { start: '', end: '' }), [field]: v };
		emitChange(next);
	};

	// prefer explicit labels passed in `labels`, otherwise use static defaults
	const finalStartLabel = (labels && labels.startLabel);
	const finalEndLabel = (labels && labels.endLabel);

	// determine locale using Moment (we use Moment for parsing/formatting)
	const active = getActiveLanguage();
	Moment.locale(active);

	// compute inputFormat similar to DatePickers.getDateFormat using Moment
	const getDateFormat = () => {
		let fmt = Moment.localeData(active).longDateFormat('L') || 'YYYY-MM-DD';
		fmt = fmt.replace('YYYY', 'yyyy');
		fmt = fmt.replace('DD', 'dd');
		return fmt;
	};

	const inputFormat = getDateFormat();

	return (
		<>
			<GlobalStyles styles={{ '.MuiPickersPopper-root': { zIndex: 1000000000 + ' !important' } }} />
			<LocalizationProvider
				dateAdapter={AdapterMoment}
				adapterLocale={getNormalizedActiveLanguage()}
				localeText={(() => {
					const pl = getPickersLocale(getNormalizedActiveLanguage());
					return pl?.components?.MuiLocalizationProvider?.defaultProps?.localeText || undefined;
				})()}
			>
				<Box display="flex" gap={2} alignItems="center" width="100%">
					<Box flex={1}>
						<DatePicker
							label={finalStartLabel}
							value={current.start ? Moment(current.start, 'YYYY-MM-DD') : null}
							onChange={(date) => {
								const val = date ? date.format('YYYY-MM-DD') : '';
								setField('start', val);
							}}
							disabled={disabled}
							slots={{ textField: TextField }}
							slotProps={{ textField: { fullWidth: true } }}
						/>
					</Box>

					<Box flex={1}>
						<DatePicker
							label={finalEndLabel}
							value={current.end ? Moment(current.end, 'YYYY-MM-DD') : null}
							onChange={(date) => {
								const val = date ? date.format('YYYY-MM-DD') : '';
								setField('end', val);
							}}
							disabled={disabled}
							minDate={current.start ? Moment(current.start, 'YYYY-MM-DD') : undefined}
							slots={{ textField: TextField }}
							slotProps={{ textField: { fullWidth: true } }}
						/>
					</Box>
				</Box>
			</LocalizationProvider></>
	);
};

export default StartEndInputs;
